/**
 * Top-bar clocks cluster.
 *
 * Renders the 0–4 operator-configured clocks from the active Profile's
 * top-level ``clocks`` working-state slice (ADR-033 §8; REQ-UI-085). Each clock
 * shows its label + ``HH:MM[:SS]`` (mono, small) inside a thin gray outline box
 * (a compact "digital clock" affordance that reads at a glance in the dense
 * 48 px top bar). Times are rendered via ``Intl.DateTimeFormat`` against the
 * clock's IANA ``timezone`` — DST-correct and tz-database-free on the server
 * (the BFF trusts the closed UI timezone list; ADR-033 §8). ``hour_cycle``
 * (``h23`` 24-hour / ``h12`` 12-hour) and ``show_seconds`` are honoured
 * per-clock.
 *
 * Time only: the clocks are deliberately time-only — the ``show_date`` slice is
 * an unused POST-MVP hook (still carried in the schema/types) that the top bar
 * does not render, keeping each clock compact in the 48 px bar.
 *
 * A single shared 1 Hz ``setInterval`` advances one ``now`` ``Date`` in state
 * (one timer for the whole cluster, not one per clock), cleared on unmount.
 * When no clocks are configured the component renders **nothing at all** —
 * no DOM, no reserved space — so the default top bar is unchanged until an
 * operator adds a clock (the editor lands in a later increment).
 *
 * Accessibility — the cluster carries a static ``aria-label="Clocks"`` and the
 * per-second-changing time text is marked ``aria-hidden``. Rationale: a
 * coordinate/time readout that changes every second would flood a live region
 * if announced; an operator does not consume wall-clock time through a screen
 * reader, and a polite region that re-announces each tick is a known AT
 * antipattern. The static label signals the cluster's presence; the dynamic
 * values stay visual-only (the same posture as the map cursor readout). The
 * clock label + timezone are part of the ``aria-label`` so an AT user still
 * learns *which* zones are shown without hearing the ticking value.
 *
 * Cross-references:
 *   - REQ-UI-085 (top-bar clocks); ADR-033 §8.
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md §Top bar.
 *   - docs/engineering/detail/storyboards/shared/design-system.md (mono token).
 */
import { useEffect, useMemo, useState } from "react";
import { usePreferences } from "@/preferences/preferences-context";
import type { ClockSettings } from "@/auth/session-types";

/** Refresh cadence for the shared tick (ms). One second keeps the seconds
 *  field live without busy-spinning. */
const TICK_MS = 1000;

/**
 * Render the configured top-bar clocks, or ``null`` when none are set.
 */
export function Clocks(): React.ReactElement | null {
  const clocks = usePreferences().workingState.clocks ?? [];
  // A single shared clock source for the whole cluster (REQ-UI-085): one
  // interval advances ``now`` once per second; every clock derives its own
  // zoned rendering from it. The timer is only armed when there is at least
  // one clock to drive (an empty cluster renders nothing, so it needs no
  // ticking).
  const [now, setNow] = useState<Date>(() => new Date());
  const hasClocks = clocks.length > 0;
  useEffect(() => {
    if (!hasClocks) {
      return;
    }
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, [hasClocks]);

  if (!hasClocks) {
    return null;
  }
  return (
    <div
      // ``ml-2`` adds clear separation from the product wordmark on top of the
      // header's ``gap-3`` so the left-most clock is not crowded against the
      // title (REQ-UI-085; chrome-spec.md §Top bar). Only renders when ≥1 clock
      // is configured, so the default bar keeps its tight wordmark spacing.
      className="ml-2 flex items-center gap-2"
      data-testid="top-bar-clocks"
      aria-label="Clocks"
    >
      {clocks.map((clock, index) => (
        <ClockCell key={index} clock={clock} now={now} />
      ))}
    </div>
  );
}

/**
 * One clock in the cluster. Memoizes its ``Intl.DateTimeFormat`` instance on the
 * clock's settings so a tick only re-runs ``.format(now)``, not formatter
 * construction. The visible text is ``aria-hidden`` (see the module a11y note);
 * the surrounding cell carries a static descriptive ``aria-label`` (label +
 * timezone, no live value). The cell is a thin gray outline box (``border-border``
 * rounded with tight padding) — a compact digital-clock affordance for the dense
 * top bar; the box is purely presentational and does not alter the aria treatment.
 */
function ClockCell({ clock, now }: { clock: ClockSettings; now: Date }): React.ReactElement {
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        timeZone: clock.timezone,
        hourCycle: clock.hour_cycle,
        hour: "2-digit",
        minute: "2-digit",
        second: clock.show_seconds ? "2-digit" : undefined,
      }),
    [clock.timezone, clock.hour_cycle, clock.show_seconds],
  );

  const label = clock.label ?? "";
  const time = timeFormatter.format(now);
  // Static descriptor for AT: identifies the clock without the ticking value.
  const ariaLabel = label !== "" ? `${label} (${clock.timezone})` : clock.timezone;

  return (
    <div
      className="rounded-sm border border-border bg-bg-surface/40 px-1.5 py-0.5 leading-none"
      data-testid="top-bar-clock"
      aria-label={ariaLabel}
    >
      <span className="font-mono text-meta tabular-nums text-text-secondary" aria-hidden="true">
        {label !== "" ? `${label} ${time}` : time}
      </span>
    </div>
  );
}
